// @flow

import * as React from 'react';

import type { Contract } from '@gandi/contracts';
import type { TranslatorProps } from '@gandi/translate';
import { Alert, Base, CheckBox } from '@gandi/ui';

import styles from './ResellerContracts.scss';

type Props = {
  translator: TranslatorProps,
  onChange: (checked: boolean) => mixed,
  failure: boolean,
  contracts: Array<Contract>,
};

function ResellerContracts(props: Props): React.Node {
  const {
    translator: { __ },
    contracts,
    onChange,
    failure,
  } = props;

  const handleChange = React.useCallback(
    (evt: SyntheticInputEvent<>) => {
      const {
        target: { checked },
      } = evt;
      onChange(checked);
    },
    [onChange],
  );

  if (failure) {
    return (
      <Base marginBottom="base">
        <Alert
          message={[
            __('We were unable to retrieve mandatory contracts at this time.'),
            __(
              'Please try again later. If the problem persists, contact customer support.',
            ),
          ]}
          intent="danger"
        />
      </Base>
    );
  }

  const name = 'reseller-contracts';

  return (
    <Base padding="small" backgroundColor="surface" aria-describedby={name}>
      <Base weight="semibold" marginBottom="xsmall" id={name}>
        {__('You must read and accept the reseller contract')}
      </Base>
      {contracts.map(contract => {
        const key = `${name}-${contract.identifier}`;
        return (
          <Base marginBottom="xsmall" key={key}>
            <CheckBox name={key} onChange={handleChange}>
              {contract.description || contract.label}{' '}
              {contract.href && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={contract.href}
                  className={styles.link}
                >
                  {__('See')}
                </a>
              )}
            </CheckBox>
          </Base>
        );
      })}
    </Base>
  );
}

export default (React.memo<Props>(
  ResellerContracts,
): React.ComponentType<Props>);
